import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RelapseStateService } from '@app/tvf/relapse/services/relapse.state';
import { RelapseService } from '@app/tvf/relapse/services/relapse.service';
import { ToastrService } from 'ngx-toastr';
import { TvfRequestsService } from '@app/tvf/services/tvf-requests.service';
import { take } from 'rxjs/operators';

export interface TradeQuestion {
  question: string;
  answerSeller: string;
  answerInternal: string | null;
  reasonInternal: string | null;
}

export interface TradeHistory {
  tradeId: string;
  dateSreening: string;
  classification: string;
  questionDivergence: boolean;
  questions: TradeQuestion[];
}

export interface ApiTradeQuestion {
  question: string;
  answerSeller: string;
  answerInternal: string | null;
  reasonInternal: string | null;
}

export interface ApiTradeHistory {
  tradeId: string;
  dataTriagem: string;
  classificacao: string;
  divergenciaQuestoes: boolean;
  questoes: ApiTradeQuestion[];
}

export interface ApiTradeHistoryResponse {
  success: boolean;
  errors: string[];
  data: ApiTradeHistory[];
}

@Component({
  selector: 'app-dialog-history-imei-trades',
  templateUrl: './dialog-history-imei-trades.component.html',
  styleUrls: ['./dialog-history-imei-trades.component.sass'],
})
export class DialogHistoryImeiTradesComponent implements OnInit {
  imei = '';

  trades: TradeHistory[] = [];
  evidenceImagesByTrade: { [tradeId: string]: string[] } = {};
  evidenceLoadingByTrade: { [tradeId: string]: boolean } = {};
  evidenceErrorByTrade: { [tradeId: string]: boolean } = {};

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<DialogHistoryImeiTradesComponent>,
    @Optional() private _relapseStateService?: RelapseStateService,
    @Optional() private _relapseService?: RelapseService,
    @Optional() private _toastr?: ToastrService,
    @Optional() private _requestsService?: TvfRequestsService
  ) {
    if (data && data.imei) {
      this.imei = data.imei;
    } else if (this._relapseStateService) {
      this.imei =
        this._relapseStateService.getImeiBiped() ||
        this._relapseStateService.getSelectedTrade()?.imei ||
        '';
    }
  }

  loadEvidenceImages(tradeId: string): void {
    if (
      !tradeId ||
      !this._requestsService ||
      this.evidenceLoadingByTrade[tradeId] ||
      Object.prototype.hasOwnProperty.call(this.evidenceImagesByTrade, tradeId)
    ) {
      return;
    }

    this.evidenceLoadingByTrade[tradeId] = true;
    this.evidenceErrorByTrade[tradeId] = false;

    this._requestsService
      .getGalleryByTradeId(tradeId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.evidenceImagesByTrade[tradeId] = response?.stiImages || [];
          this.evidenceLoadingByTrade[tradeId] = false;
        },
        error: () => {
          this.evidenceLoadingByTrade[tradeId] = false;
          this.evidenceErrorByTrade[tradeId] = true;
        },
      });
  }

  ngOnInit(): void {
    if (Array.isArray(this.data?.trades)) {
      this.setTrades(this.data.trades);
      return;
    }

    if (this.imei && this._relapseService) {
      this._relapseService.getHistoryTrade(this.imei).subscribe({
        next: (response: ApiTradeHistoryResponse) => {
          if (response && response.success && response.data) {
            this.setTrades(response.data);
          } else {
            this.trades = [];
          }
        },
        error: (err) => {
          this._toastr?.error(err?.errors?.[0] || 'Erro ao buscar histórico do IMEI');
          this.trades = [];
        },
      });
    }
  }

  private setTrades(trades: ApiTradeHistory[]): void {
    this.trades = trades.map((item) => ({
      tradeId: item.tradeId,
      dateSreening: item.dataTriagem,
      classification: item.classificacao,
      questionDivergence: item.divergenciaQuestoes,
      questions: (item.questoes || []).map((q) => ({
        question: q.question,
        answerSeller: q.answerSeller,
        answerInternal: q.answerInternal,
        reasonInternal: q.reasonInternal,
      })),
    }));
  }

  trackByTrade(_: number, trade: TradeHistory): string {
    return trade.tradeId;
  }

  getDivergences(questions: TradeQuestion[]): TradeQuestion[] {
    return questions.filter(
      (q) =>
        (q.answerSeller || '').trim().toLowerCase() !==
        (q.answerInternal || '').trim().toLowerCase()
    );
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  formatDateSreening(date: string): string {
    if (!date) {
      return '-';
    }

    const [data, hora] = date.split(' ');

    return `${data} às ${hora}`;
  }
}
